// =============================================================================
// whatsappMarkup.ts — tokenizador da formatação do WhatsApp (F16-S56).
//
// O WhatsApp usa marcadores de um caractere: *negrito*, _itálico_, ~riscado~ e
// `mono`. As bolhas do live chat renderizavam `message.content` como texto puro,
// então o atendente via os marcadores crus — o oposto do que o cidadão vê.
//
// SEGURANÇA: este módulo devolve DADOS (tokens), nunca HTML. O conteúdo vem do
// cidadão via WhatsApp — entrada não confiável. Quem renderiza monta nós React a
// partir dos tokens; `dangerouslySetInnerHTML` está fora de questão.
//
// Sem DOM: função pura, testável em node puro (convenção dos testes do web).
// =============================================================================

export type MarkupStyle = 'bold' | 'italic' | 'strike' | 'mono';

export interface MarkupToken {
  /** Trecho literal de texto. Nunca vazio. */
  text: string;
  /** Estilos ativos neste trecho, do mais externo para o mais interno. */
  styles: MarkupStyle[];
}

const MARKERS: Readonly<Record<string, MarkupStyle>> = {
  '*': 'bold',
  _: 'italic',
  '~': 'strike',
  '`': 'mono',
};

/**
 * Converte texto com marcadores do WhatsApp numa lista de trechos estilizados.
 *
 * Regras (espelham o comportamento do app do WhatsApp):
 * - Marcador só abre se houver o mesmo marcador adiante com conteúdo no meio.
 *   `*a*` vira negrito; `**` e `*` solto ficam literais — texto nunca é perdido.
 * - Estilos aninham: `*negrito _e itálico_*`.
 * - Dentro de `mono` não há aninhamento — o conteúdo é literal, como no WhatsApp.
 */
export function parseWhatsappMarkup(input: string): MarkupToken[] {
  if (!input) return [];
  return parseInto(input, []);
}

function parseInto(text: string, styles: readonly MarkupStyle[]): MarkupToken[] {
  const out: MarkupToken[] = [];
  let buffer = '';
  let i = 0;

  const flush = (): void => {
    if (buffer) {
      out.push({ text: buffer, styles: [...styles] });
      buffer = '';
    }
  };

  while (i < text.length) {
    const ch = text[i] as string;
    const style = MARKERS[ch];

    // Marcador já ativo não reabre — evita recursão infinita em `*a*b*`.
    if (style !== undefined && !styles.includes(style)) {
      const close = text.indexOf(ch, i + 1);
      // close > i + 1 garante conteúdo não-vazio entre os marcadores.
      if (close > i + 1) {
        flush();
        const inner = text.slice(i + 1, close);
        const nested = [...styles, style];
        if (style === 'mono') {
          out.push({ text: inner, styles: nested });
        } else {
          out.push(...parseInto(inner, nested));
        }
        i = close + 1;
        continue;
      }
    }

    buffer += ch;
    i += 1;
  }

  flush();
  return out;
}
